<!-- Data-widget visual (Bloque 1): KPI · Gauge · Chart · Table. Renders a
     data-bound widget from already-resolved style + the live value/series
     fed by design_render (host-agnostic, like SynShape). Lightweight SVG/
     HTML — no chart lib — so the same geometry mirrors in the View SDK
     (view-html.ts) for export/thumbnails. A widget is a normal `shape`
     node (props.shape = kpi|gauge|chart|table), so it groups, animates and
     saves as a reusable component like any other shape. -->
<template>
  <!-- ADR 47 F3 — HMI alarm annunciator (self-resolving; live count + priority). -->
  <AlarmBanner v-if="kind === 'alarm'" :p="p" :live="live" />

  <!-- KPI: big formatted number + unit, colour-coded by thresholds. -->
  <div v-else-if="kind === 'kpi'" class="w-kpi" :style="{ color: fill || 'var(--color-ink)' }">
    <span v-if="p.label" class="w-kpi-label">{{ p.label }}</span>
    <span class="w-kpi-row">
      <span class="w-kpi-num" :style="{ fontSize: (p.fontSize || 34) + 'px', color: kpiColor }">{{ kpiText }}</span>
      <span v-if="p.unit" class="w-kpi-unit">{{ p.unit }}</span>
    </span>
  </div>

  <!-- Gauge: semicircular arc, value fills the arc; zones colour it. -->
  <svg v-else-if="kind === 'gauge'" class="w-svg" viewBox="0 0 100 64" preserveAspectRatio="xMidYMid meet">
    <path d="M10,50 A40,40 0 0 1 90,50" fill="none" :stroke="trackColor" stroke-width="9" stroke-linecap="round" />
    <path :d="gaugeArc" fill="none" :stroke="gaugeColor" stroke-width="9" stroke-linecap="round" />
    <text x="50" y="47" text-anchor="middle" class="w-g-num" :fill="fill || '#111827'">{{ gaugeText }}</text>
    <text v-if="p.label || p.unit" x="50" y="60" text-anchor="middle" class="w-g-sub">{{ p.label || p.unit }}</text>
  </svg>

  <!-- Circular progress: full ring filled to the value fraction. -->
  <svg v-else-if="kind === 'progress'" class="w-svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
    <circle cx="50" cy="50" :r="ringR" fill="none" :stroke="trackColor" :stroke-width="ringW" />
    <circle
      cx="50" cy="50" :r="ringR" fill="none" :stroke="gaugeColor" :stroke-width="ringW" stroke-linecap="round"
      :stroke-dasharray="ringCirc" :stroke-dashoffset="ringOffset" transform="rotate(-90 50 50)"
    />
    <text x="50" y="50" text-anchor="middle" dominant-baseline="central" class="w-p-num" :fill="fill || '#111827'">{{ gaugeText }}</text>
    <text v-if="p.label" x="50" y="68" text-anchor="middle" class="w-g-sub">{{ p.label }}</text>
  </svg>

  <!-- Level: vertical tank/silo fill, bottom-up to the value fraction. The
       fill height tracks (value-min)/(max-min); zones colour it. -->
  <div v-else-if="kind === 'level'" class="w-level" :style="{ background: trackColor }">
    <div class="w-level-fill" :style="{ height: (gaugeFrac * 100).toFixed(1) + '%', background: gaugeColor }"></div>
    <div v-if="p.showValue !== false" class="w-level-num" :style="{ color: fill || '#111827' }">{{ gaugeText }}<span v-if="p.unit" class="w-level-unit">{{ p.unit }}</span></div>
  </div>

  <!-- Chart: sparkline (line / area / bars) over the recent series. -->
  <svg v-else-if="kind === 'chart'" class="w-svg" :viewBox="`0 0 ${CW} ${CH}`" preserveAspectRatio="none">
    <template v-if="(p.chartType ?? 'line') === 'bar'">
      <rect v-for="(b, i) in bars" :key="i" :x="b.x" :y="b.y" :width="b.w" :height="b.h" :fill="chartColor" rx="0.6" />
    </template>
    <template v-else>
      <polygon v-if="(p.chartType ?? 'line') === 'area'" :points="areaPoints" :fill="chartColor" fill-opacity="0.16" />
      <polyline :points="linePoints" fill="none" :stroke="chartColor" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" />
    </template>
  </svg>

  <!-- Image carousel: cycles through props.images (auto-advances in Run). -->
  <div v-else-if="kind === 'carousel'" class="w-carousel">
    <img v-if="images.length" :src="images[carIdx % images.length]" class="w-car-img" :style="`object-fit:${(p.fit as string) || 'cover'}`" />
    <div v-else class="w-car-empty">No images</div>
    <template v-if="images.length > 1">
      <button type="button" class="w-car-nav left" @click.stop="carIdx = (carIdx - 1 + images.length) % images.length">‹</button>
      <button type="button" class="w-car-nav right" @click.stop="carIdx = (carIdx + 1) % images.length">›</button>
      <div class="w-car-dots">
        <span v-for="(_, i) in images" :key="i" class="w-car-dot" :class="{ on: i === carIdx % images.length }"></span>
      </div>
    </template>
  </div>

  <!-- Video: HTML5 <video> for an MP4/stream URL. -->
  <video
    v-else-if="kind === 'video'"
    class="w-video"
    :src="(p.src as string) || undefined"
    :controls="p.controls !== false"
    :autoplay="!!p.autoplay && live"
    :loop="p.loop !== false"
    :muted="p.muted !== false"
    playsinline
  ></video>

  <!-- Multi-column data table: columns from variables + computed expressions
       (resolved server-side by design_render). -->
  <div v-else-if="hasColumns" class="w-table" :style="tableBox">
    <table>
      <thead><tr>
        <th v-if="gridShowTs" class="w-t-time">Time</th>
        <th v-for="c in gridCols" :key="c.key" :style="colStyle(c)">{{ c.label }}</th>
      </tr></thead>
      <tbody>
        <tr v-for="(row, i) in gridRows" :key="i" :class="{ 'w-t-click': live }" @pointerdown="onRowDown" @click="onRow(i)">
          <td v-if="gridShowTs" class="w-t-time">{{ gridTime(i) }}</td>
          <td v-for="(cell, j) in row" :key="j" class="w-t-val" :style="cellStyle(j)">
            <input v-if="gridCols[j]?.format === 'bool'" type="checkbox" disabled :checked="cellBool(cell)" />
            <template v-else>{{ fmtCell(cell, gridCols[j]) }}</template>
          </td>
        </tr>
        <tr v-if="!gridRows.length"><td :colspan="Math.max(1, gridCols.length + (gridShowTs ? 1 : 0))" class="w-t-empty">{{ gridErr || (live ? 'No data' : 'Configura los datos de la tabla') }}</td></tr>
      </tbody>
    </table>
  </div>

  <!-- Table (legacy single-variable): recent readings (time + value). -->
  <div v-else class="w-table" :style="tableBox">
    <table>
      <thead><tr><th v-if="showTs">Time</th><th>{{ p.valueLabel || 'Value' }}</th></tr></thead>
      <tbody>
        <tr v-for="(row, i) in tableRows" :key="i">
          <td v-if="showTs" class="w-t-time">{{ row.t }}</td>
          <td class="w-t-val">{{ row.v }}</td>
        </tr>
        <tr v-if="!tableRows.length"><td :colspan="showTs ? 2 : 1" class="w-t-empty">No data</td></tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useCanvasHost } from './canvas-host';
const AlarmBanner = useCanvasHost().alarm;

export interface SeriesPoint { t: number; v: number }
export interface Threshold { op?: 'gt' | 'ge' | 'lt' | 'le'; value: number; color: string }

const emit = defineEmits<{ (e: 'row-click', ctx: Record<string, unknown>): void }>();
const props = withDefaults(
  defineProps<{
    kind: string;
    p: Record<string, any>;
    fill?: string;
    stroke?: string;
    // Resolved gauge/level/progress fill colour (eff override → raw prop). Lets
    // a state or a dynamic binding recolour the bar; raw props alone can't.
    color?: string;
    value?: number | null;
    series?: SeriesPoint[];
    table?: { columns: Array<{ key: string; label: string }>; rows: Array<Array<number | string | boolean | null>>; ts?: number[]; error?: string } | null;
    live?: boolean;
  }>(),
  { live: false },
);

const CW = 100;
const CH = 46;

// Design-time sample so the editor looks meaningful before Run/binding.
const SAMPLE_SERIES: SeriesPoint[] = Array.from({ length: 24 }, (_, i) => ({ t: i, v: 50 + 28 * Math.sin(i / 3) + (i % 4) * 2 }));

function fmt(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const d = props.p.decimals;
  // Explicit `decimals` prop wins. Otherwise format naturally: integers (e.g. a
  // count) show with no decimals; fractional values keep up to 3 — so a count
  // never shows "1440.0".
  return d != null
    ? n.toFixed(Number(d))
    : n.toLocaleString(undefined, { maximumFractionDigits: 3 });
}

const thresholds = computed<Threshold[]>(() => (Array.isArray(props.p.thresholds) ? props.p.thresholds : []) as Threshold[]);

// ── Scalar (KPI / gauge) ────────────────────────────────────────────────
const scalar = computed<number | null>(() => {
  if (props.value != null) return props.value;
  return props.live ? null : 72.4; // editor sample
});
const kpiText = computed(() => (scalar.value == null ? '—' : fmt(scalar.value)));
function cmp(v: number, op: Threshold['op'], t: number): boolean {
  return op === 'gt' ? v > t : op === 'lt' ? v < t : op === 'le' ? v <= t : v >= t; // default ge
}
const kpiColor = computed(() => {
  let c = props.fill || 'var(--color-ink)';
  const v = scalar.value;
  if (v == null) return c;
  for (const t of thresholds.value) if (cmp(v, t.op ?? 'ge', Number(t.value))) c = t.color; // last match wins
  return c;
});

// ── Gauge geometry ──────────────────────────────────────────────────────
const gMin = computed(() => Number(props.p.min ?? 0));
const gMax = computed(() => { const m = Number(props.p.max ?? 100); return m === gMin.value ? gMin.value + 1 : m; });
const gaugeText = computed(() => (scalar.value == null ? '—' : fmt(scalar.value)));
const gaugeFrac = computed(() => {
  const v = scalar.value ?? (props.live ? gMin.value : gMin.value + (gMax.value - gMin.value) * 0.65);
  return Math.max(0, Math.min(1, (v - gMin.value) / (gMax.value - gMin.value)));
});
const gaugeArc = computed(() => {
  const f = gaugeFrac.value;
  const theta = Math.PI * (1 - f); // 180°→0°
  const x = 50 + 40 * Math.cos(theta);
  const y = 50 - 40 * Math.sin(theta);
  const large = 0;
  if (f <= 0.0001) return 'M10,50 A40,40 0 0 1 10.01,50';
  return `M10,50 A40,40 0 ${large} 1 ${x.toFixed(2)},${y.toFixed(2)}`;
});
const trackColor = computed(() => props.stroke || '#e5e7eb');
// Circular progress ring geometry (shares gaugeFrac/gaugeColor/gaugeText).
const ringR = 40, ringW = 10;
const ringCirc = 2 * Math.PI * ringR;
const ringOffset = computed(() => ringCirc * (1 - gaugeFrac.value));
const gaugeColor = computed(() => {
  let c = props.color || (props.p.color as string) || '#2c70b9';
  const v = scalar.value ?? gMin.value + (gMax.value - gMin.value) * gaugeFrac.value;
  const zs = [...thresholds.value].sort((a, b) => Number(a.value) - Number(b.value));
  for (const z of zs) if (v >= Number(z.value)) c = z.color; // highest crossed zone
  return c;
});

// ── Series (chart / table) ──────────────────────────────────────────────
const data = computed<SeriesPoint[]>(() => {
  if (props.series && props.series.length) return props.series;
  return props.live ? [] : SAMPLE_SERIES;
});
const chartColor = computed(() => (props.p.color as string) || props.stroke || '#2c70b9');
const scaled = computed(() => {
  const d = data.value;
  if (!d.length) return [] as Array<{ x: number; y: number }>;
  const vals = d.map((s) => s.v);
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  return d.map((s, i) => ({
    x: d.length > 1 ? (i / (d.length - 1)) * CW : CW / 2,
    y: CH - 2 - ((s.v - min) / span) * (CH - 4),
  }));
});
const linePoints = computed(() => scaled.value.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' '));
const areaPoints = computed(() => (scaled.value.length ? `0,${CH} ${linePoints.value} ${CW},${CH}` : ''));
const bars = computed(() => {
  const d = data.value;
  if (!d.length) return [];
  const vals = d.map((s) => s.v);
  const min = Math.min(...vals, 0), max = Math.max(...vals);
  const span = max - min || 1;
  const slot = CW / d.length;
  const w = Math.max(0.5, slot * 0.7);
  return d.map((s, i) => {
    const h = ((s.v - min) / span) * (CH - 2);
    return { x: i * slot + (slot - w) / 2, y: CH - h, w, h };
  });
});

// ── Table rows ──────────────────────────────────────────────────────────
// ── Image carousel ──────────────────────────────────────────────────────
const images = computed<string[]>(() => (Array.isArray(props.p.images) ? props.p.images.filter((s: unknown) => !!s) : []) as string[]);
const carIdx = ref(0);
let carTimer: ReturnType<typeof setInterval> | null = null;
// Auto-advance only in Run mode (live); edit mode stays on the first image.
// Armed via a WATCH, not just onMounted: the canvas REUSES this component
// when flipping edit ⇄ Run (props.live changes in place), so a mount-only
// timer never started on entering Run and kept running after leaving it.
function setupCarTimer(): void {
  if (carTimer) { clearInterval(carTimer); carTimer = null; }
  if (!props.live) { carIdx.value = 0; return; } // back to edit → first image
  const ms = Math.max(1000, Number(props.p.interval ?? 3000));
  carTimer = setInterval(() => { carIdx.value++; }, ms);
}
watch(() => [props.live, props.p.interval], setupCarTimer);
onMounted(setupCarTimer);
onBeforeUnmount(() => { if (carTimer) clearInterval(carTimer); });

const tableBox = computed(() => {
  const p = props.p;
  const col = p.borderColor || '#e5e7eb', st = p.strokeStyle || 'solid', rad = (p.radius ?? 6) + 'px';
  const s = p.borderSides as { t?: number; r?: number; b?: number; l?: number } | undefined;
  if (s) return { borderColor: col, borderStyle: st, borderRadius: rad, borderTopWidth: (s.t ?? 0) + 'px', borderRightWidth: (s.r ?? 0) + 'px', borderBottomWidth: (s.b ?? 0) + 'px', borderLeftWidth: (s.l ?? 0) + 'px' };
  return { borderColor: col, borderWidth: (p.strokeWidth ?? 1) + 'px', borderStyle: st, borderRadius: rad };
});
const showTs = computed(() => props.p.showTime !== false);

// ── Multi-column data table ───────────────────────────────────────────────
interface ColSpec { key: string; label?: string }
// Per-column presentation overlay (props.columnFormat), applied to whatever
// columns the table produces — variable spec OR a SQL/transform result.
type ColFormat = 'text' | 'number' | 'date' | 'datetime' | 'time' | 'bool';
interface ColFmt { key: string; label?: string; width?: number; align?: 'left' | 'center' | 'right'; decimals?: number; hidden?: boolean; color?: string; bg?: string; bold?: boolean; italic?: boolean; format?: ColFormat }
interface EffCol { key: string; label: string; srcIdx: number; width?: number; align?: string; decimals?: number; color?: string; bg?: string; bold?: boolean; italic?: boolean; format?: ColFormat }
// Grid mode = a multi-column spec (variable/connector/calc), or a live result
// table (a `transform`/Avanzado query whose columns arrive at Run).
const hasColumns = computed(() => (Array.isArray(props.p.columns) && props.p.columns.length > 0) || colFmt.value.length > 0 || (props.live && !!props.table?.columns?.length));
const gridErr = computed(() => props.table?.error || '');
// Raw columns the data produced (result columns live, else the variable spec).
const baseCols = computed<{ key: string; label: string }[]>(() => {
  if (props.live && props.table?.columns?.length) return props.table.columns;
  return ((props.p.columns ?? []) as ColSpec[]).map((c) => ({ key: c.key, label: c.label || c.key }));
});
const colFmt = computed<ColFmt[]>(() => (Array.isArray((props.p as { columnFormat?: unknown }).columnFormat) ? (props.p as { columnFormat: ColFmt[] }).columnFormat : []));
// Effective columns: reorder/rename/hide per columnFormat (configured first, in
// order); unconfigured data columns are appended so nothing is silently lost.
const gridCols = computed<EffCol[]>(() => {
  const base = baseCols.value;
  if (!colFmt.value.length) return base.map((c, i) => ({ key: c.key, label: c.label, srcIdx: i }));
  const byKey = new Map(base.map((c, i) => [c.key, i] as const));
  // columnFormat IS the curated list (removed columns stay gone). Each column
  // shows even with no data yet (srcIdx -1 → placeholder) so the layout/order/
  // width are visible at design time before the query runs.
  const out: EffCol[] = [];
  for (const f of colFmt.value) {
    if (f.hidden) continue;
    const idx = byKey.get(f.key);
    out.push({ key: f.key, label: f.label || (idx !== undefined ? base[idx]!.label : f.key), srcIdx: idx ?? -1, width: f.width, align: f.align, decimals: f.decimals, color: f.color, bg: f.bg, bold: f.bold, italic: f.italic, format: f.format });
  }
  return out;
});
const gridShowTs = computed(() => props.p.showTime !== false && (props.live ? !!props.table?.ts?.length : true));
const SAMPLE_ROWS = 3;
const gridRows = computed<Array<Array<number | string | boolean | null>>>(() => {
  const cols = gridCols.value;
  if (props.live) return (props.table?.rows ?? []).map((row) => cols.map((c) => (c.srcIdx >= 0 ? (row[c.srcIdx] ?? null) : null) as number | string | boolean | null));
  if (!cols.length) return []; // columns not known yet (e.g. a query result) → no placeholder
  // Edit-time placeholder so the column layout is visible before Run.
  return Array.from({ length: SAMPLE_ROWS }, () => cols.map(() => '—'));
});
function colStyle(c: EffCol): Record<string, string> {
  const s: Record<string, string> = {};
  if (c.width) s.width = `${c.width}px`;
  if (c.align) s.textAlign = c.align;
  return s;
}
function cellStyle(j: number): Record<string, string> {
  const c = gridCols.value[j];
  const s: Record<string, string> = {};
  if (c?.align) s.textAlign = c.align;
  if (c?.color) s.color = c.color;
  if (c?.bg) s.background = c.bg;
  if (c?.bold) s.fontWeight = '600';
  if (c?.italic) s.fontStyle = 'italic';
  return s;
}
function gridTime(i: number): string {
  const t = props.table?.ts?.[i];
  return typeof t === 'number' ? new Date(t).toLocaleTimeString() : '—';
}
// Epoch (s/ms/µs/ns) or ISO/date string → ms, scaled by magnitude.
function toMs(v: unknown): number | null {
  const s = String(v ?? '');
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    let n = Number(s); if (!Number.isFinite(n)) return null;
    const a = Math.abs(n);
    if (a >= 1e16) n /= 1e6; else if (a >= 1e14) n /= 1e3; else if (a < 1e11) n *= 1e3;
    return n;
  }
  const d = new Date(s); return Number.isNaN(d.getTime()) ? null : d.getTime();
}
function cellBool(cell: unknown): boolean {
  return cell === true || cell === 1 || cell === '1' || cell === 't' || String(cell).toLowerCase() === 'true';
}
function fmtCell(cell: number | string | boolean | null, col?: EffCol): string {
  if (cell == null) return '—';
  const f = col?.format;
  if (f === 'bool') return ''; // rendered as a checkbox in the template
  if (f === 'date' || f === 'datetime' || f === 'time') {
    const ms = toMs(cell); if (ms == null) return String(cell);
    const d = new Date(ms);
    return f === 'date' ? d.toLocaleDateString() : f === 'time' ? d.toLocaleTimeString() : d.toLocaleString();
  }
  if (typeof cell === 'number') {
    if (!Number.isFinite(cell)) return '—';
    return col?.decimals != null ? cell.toFixed(col.decimals) : String(cell);
  }
  if (f === 'number') { const n = Number(cell); return Number.isFinite(n) ? (col?.decimals != null ? n.toFixed(col.decimals) : String(n)) : String(cell); }
  return String(cell);
}
// Row click (Run mode) → emit the row as a {column: cell} dict so a click event
// (set_variable/navigate, with {column} interpolation) can cross-filter or
// drill down. Stop the pointer so the shape's generic click doesn't also fire.
function onRowDown(e: PointerEvent): void { if (props.live) e.stopPropagation(); }
function onRow(i: number): void {
  if (!props.live) return;
  const row = gridRows.value[i];
  if (!row) return;
  const ctx: Record<string, unknown> = {};
  gridCols.value.forEach((c, idx) => { ctx[c.key] = row[idx]; });
  const ts = props.table?.ts?.[i];
  if (ts != null) ctx.ts = ts;
  emit('row-click', ctx);
}

const tableRows = computed(() => {
  const n = Math.max(1, Math.min(50, Number(props.p.rows ?? 8)));
  return [...data.value].reverse().slice(0, n).map((s) => ({
    t: typeof s.t === 'number' && s.t > 1e6 ? new Date(s.t).toLocaleTimeString() : String(s.t),
    v: fmt(s.v),
  }));
});
</script>

<style scoped>
.w-svg { width: 100%; height: 100%; display: block; }
.w-kpi { width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px; overflow: hidden; }
.w-kpi-label { font-size: .7rem; opacity: .65; line-height: 1; }
.w-kpi-row { display: flex; align-items: baseline; gap: 4px; }
.w-kpi-num { font-weight: 700; line-height: 1; }
.w-kpi-unit { font-size: .72em; opacity: .7; }
.w-g-num { font-size: 17px; font-weight: 700; }
.w-p-num { font-size: 18px; font-weight: 700; }
.w-g-sub { font-size: 7px; fill: #9ca3af; }
.w-level { position: relative; width: 100%; height: 100%; overflow: hidden; border-radius: inherit; display: flex; align-items: flex-end; }
.w-level-fill { width: 100%; transition: height 0.4s ease; }
.w-level-num { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; gap: 2px; font-size: 0.8rem; font-weight: 700; }
.w-level-unit { font-size: 0.7em; opacity: 0.8; }
.w-table { width: 100%; height: 100%; overflow: auto; border: 1px solid #e5e7eb; border-radius: 6px; background: #fff; }
.w-table table { width: 100%; border-collapse: collapse; font-size: .72rem; }
.w-table th { text-align: left; font-weight: 600; color: #6b7280; background: #f8fafc; padding: 3px 8px; position: sticky; top: 0; border-bottom: 1px solid #e5e7eb; }
.w-table td { padding: 3px 8px; border-bottom: 1px solid #f1f5f9; color: #374151; }
.w-table tr.w-t-click { cursor: pointer; }
.w-table tr.w-t-click:hover td { background: var(--color-brand-50, #eef4fb); }
.w-t-val { text-align: right; font-variant-numeric: tabular-nums; }
.w-t-time { color: #6b7280; font-variant-numeric: tabular-nums; }
.w-t-empty { text-align: center; color: #9ca3af; }
.w-carousel { position: relative; width: 100%; height: 100%; overflow: hidden; border-radius: 6px; background: #0f172a; }
.w-car-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.w-car-empty { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #94a3b8; font-size: .8rem; background: #e5e7eb; }
.w-car-dots { position: absolute; bottom: 6px; left: 0; right: 0; display: flex; justify-content: center; gap: 4px; }
.w-car-dot { width: 6px; height: 6px; border-radius: 50%; background: rgba(255,255,255,.45); }
.w-car-dot.on { background: #fff; }
.w-video { width: 100%; height: 100%; object-fit: contain; display: block; background: #0f172a; border-radius: 6px; }
.w-car-nav { position: absolute; top: 50%; transform: translateY(-50%); width: 22px; height: 30px; border: none; background: rgba(0,0,0,.35); color: #fff; font-size: 1.1rem; line-height: 1; cursor: pointer; border-radius: 4px; }
.w-car-nav.left { left: 4px; } .w-car-nav.right { right: 4px; }
.w-car-nav:hover { background: rgba(0,0,0,.55); }
</style>
